import { useEffect } from 'react';
import type { ReCaptchaInstance } from 'recaptcha-v3';
import { load } from 'recaptcha-v3';
import { Environment, getCurrentEnvironment } from '@/utils/environment';

/**
 * reCAPTCHA v3 for the signup funnel. The action names (`signup` for entry,
 * `signup_signup` for registration) are a server contract — `libraries/recaptcha`
 * verifies them.
 */
export type RecaptchaAction = 'signup' | 'signup_signup';

/**
 * The v3 site key is public; only production-like environments have one — the
 * server bypasses verification elsewhere, and `executeRecaptcha` short-circuits
 * to match.
 */
const RECAPTCHA_SITE_KEY = '6Lct874UAAAAAEf0EU8GEaiL2EINajRcotlzTEgO';

function getSiteKey(): string | undefined {
  return getCurrentEnvironment() === Environment.DEVELOPMENT ? undefined : RECAPTCHA_SITE_KEY;
}

let instance: ReCaptchaInstance | undefined;
let loading: Promise<void> | undefined;

/**
 * Load the reCAPTCHA script and render the badge. No-op without a site key.
 * Single-flight: concurrent callers share one `load`; a failed load clears the
 * memo so a later attempt can retry.
 */
export function loadRecaptcha(): Promise<void> {
  const siteKey = getSiteKey();
  if (siteKey == null || instance != null) {
    return Promise.resolve();
  }
  loading ??= load(siteKey).then(
    (loaded) => {
      instance = loaded;
    },
    (error: unknown) => {
      loading = undefined;
      throw error;
    },
  );
  return loading;
}

/**
 * Execute a reCAPTCHA action and return the token, lazily loading the script.
 * Resolves `undefined` without a site key — callers omit the token and the
 * server skips verification.
 */
export async function executeRecaptcha(action: RecaptchaAction): Promise<string | undefined> {
  if (getSiteKey() == null) {
    return undefined;
  }

  if (instance == null) {
    await loadRecaptcha();
  }

  return instance?.execute(action);
}

export function showRecaptchaBadge(): void {
  instance?.showBadge();
}

export function hideRecaptchaBadge(): void {
  instance?.hideBadge();
}

/**
 * Preload the script and show the badge while the signup page is mounted,
 * hiding it on unmount. The badge is Google's ToS-required attribution;
 * `load` renders it, and it must not linger over the app once the visitor
 * leaves the funnel.
 */
export function useRecaptchaBadge(): void {
  useEffect(() => {
    let cancelled = false;
    loadRecaptcha()
      .then(() => {
        if (cancelled) {
          // Unmounted while the script was loading. `load()` renders the badge
          // as a side effect, so a badge just appeared over whatever page the
          // visitor navigated to — the cleanup below ran too early to catch it.
          hideRecaptchaBadge();
          return;
        }
        showRecaptchaBadge();
      })
      .catch(() => {
        // Script blocked/unreachable — no badge to manage. `executeRecaptcha`
        // surfaces the failure where it matters (the guarded API calls).
      });
    return () => {
      cancelled = true;
      hideRecaptchaBadge();
    };
  }, []);
}

/** Test-only: drop the loaded instance and in-flight load so tests can re-stub `load`. */
export function resetRecaptchaForTesting(): void {
  instance = undefined;
  loading = undefined;
}
